import { runsOf } from './richText';
import { cellStyleOf, colWidths, isCovered, mergeOf } from './tableLayout';

/**
 * Copied document paragraphs as HTML, for pasting into Word or Google Docs.
 *
 * Both read the clipboard's `text/html` and keep inline CSS: paragraph
 * alignment, line spacing and the space above each paragraph, and per run the
 * font, size (in points, which both use natively), weight, slant, underline,
 * strike-through and colour. A line break inside a paragraph is a `<br>`.
 *
 * The family is named, not embedded: it shows as long as the computer pasting
 * has it (Hind Siliguri, Kalpurush…), and falls back to a Bangla-capable face
 * otherwise. `fontStack` is left out on purpose — for a PDF's own font it names
 * pdf.js's in-page faces, which mean nothing outside this tab.
 *
 * A copied table (a block `{ table }` among the paragraphs) goes over as a real
 * `<table>` — column widths, rules, merged cells and each cell's own look — and
 * as tab-separated rows in the plain text, which is what spreadsheets and
 * plain text boxes expect.
 *
 * The fragment markers are what Word looks for to know where the pasted part
 * starts and ends.
 */

const escape = (text) => String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const pt = (value) => `${Math.round(Number(value) * 100) / 100}pt`;

function runCss(style) {
    const css = [];
    if (style.fontFamily) css.push(`font-family:'${String(style.fontFamily).replace(/'/g, '')}','Noto Sans Bengali',sans-serif`);
    if (style.fontSize) css.push(`font-size:${pt(style.fontSize)}`);
    css.push(`font-weight:${style.bold ? 700 : 400}`);
    css.push(`font-style:${style.italic ? 'italic' : 'normal'}`);
    const decoration = [style.underline && 'underline', style.strike && 'line-through'].filter(Boolean);
    if (decoration.length) css.push(`text-decoration:${decoration.join(' ')}`);
    if (style.color) css.push(`color:${style.color}`);
    return css.join(';');
}

function paragraphCss(paragraph) {
    const s = paragraph.style || {};
    const css = ['margin:0', `margin-top:${pt(paragraph.gapBefore || 0)}`];
    if (s.align) css.push(`text-align:${s.align}`);
    if (s.lineHeight) css.push(`line-height:${Math.round(s.lineHeight * 100)}%`);
    if (s.background && s.background !== 'transparent') css.push(`background:${s.background}`);
    return css.join(';');
}

/** A table as tab-separated rows; a line break inside a cell becomes a space. */
export function tableToText(t) {
    const rows = [];
    for (let r = 0; r < t.rows; r += 1) {
        const cells = [];
        for (let c = 0; c < t.cols; c += 1) {
            cells.push(isCovered(t, r, c) ? '' : String(t.cells?.[r]?.[c] ?? '').replace(/\s*\n\s*/g, ' '));
        }
        rows.push(cells.join('\t'));
    }
    return rows.join('\n');
}

/** What a copied block reads as in plain text: its text, or a table's rows. */
export const blockText = (block) => (block.table ? tableToText(block.table) : block.text);

function tableToHtml(t, gapBefore = 0) {
    const widths = colWidths(t);
    const rule = t.borderWidth === 0 ? 'none' : `${pt(t.borderWidth || 1)} solid ${t.borderColor || '#1C1815'}`;
    const pad = `${pt(t.padY ?? 4)} ${pt(t.padX ?? 6)}`;
    const cols = widths.map((w) => `<col style="width:${pt(w)}">`).join('');
    const rows = [];
    for (let r = 0; r < t.rows; r += 1) {
        const cells = [];
        for (let c = 0; c < t.cols; c += 1) {
            if (isCovered(t, r, c)) continue;
            const m = mergeOf(t, r, c);
            const own = cellStyleOf(t, r, c);
            let w = 0;
            for (let i = c; i < c + (m ? m.cs : 1); i += 1) w += widths[i] || 0;
            const span = (m && m.cs > 1 ? ` colspan="${m.cs}"` : '') + (m && m.rs > 1 ? ` rowspan="${m.rs}"` : '');
            const valign = own.valign ?? t.valign ?? 'top';
            const css = [`border:${rule}`, `padding:${pad}`, `width:${pt(w)}`, `vertical-align:${valign}`].join(';');
            const run = runCss({
                fontFamily: t.fontFamily,
                fontSize: own.fontSize ?? t.fontSize,
                bold: own.bold,
                italic: own.italic,
                color: t.color,
            });
            const text = String(t.cells?.[r]?.[c] ?? '');
            const inner = text ? `<span style="${run}">${escape(text).replace(/\n/g, '<br>')}</span>` : '<br>';
            cells.push(`<td${span} style="${css}"><p style="margin:0;text-align:${own.align ?? t.align ?? 'left'}">${inner}</p></td>`);
        }
        rows.push(`<tr style="height:${pt(t.rowHeights?.[r] || 0)}">${cells.join('')}</tr>`);
    }
    const css = `border-collapse:collapse;margin-top:${pt(gapBefore)};width:${pt(t.w)}`;
    return `<table style="${css}"><colgroup>${cols}</colgroup><tbody>${rows.join('')}</tbody></table>`;
}

/**
 * @param {Array<{ text: string, marks?: object[], style: object, gapBefore?: number } | { table: object, gapBefore?: number }>} paragraphs
 * @returns {string}
 */
export function paragraphsToHtml(paragraphs) {
    const body = (paragraphs || []).map((paragraph) => {
        if (paragraph.table) return tableToHtml(paragraph.table, paragraph.gapBefore);
        const obj = { ...paragraph.style, text: paragraph.text, marks: paragraph.marks };
        const runs = runsOf(obj)
            .filter((run) => run.text.length)
            .map((run) => `<span style="${runCss(run.style)}">${escape(run.text).replace(/\n/g, '<br>')}</span>`)
            .join('');
        // An empty paragraph still takes its line, as it does on the page.
        return `<p style="${paragraphCss(paragraph)}">${runs || '<br>'}</p>`;
    }).join('');
    return `<html><head><meta charset="utf-8"></head><body><!--StartFragment-->${body}<!--EndFragment--></body></html>`;
}
